import { FieldSelect } from '@dixa/lib-shared-habla';
import type { Queue } from 'lib-shared-legacy-types';
import { useCallback, useEffect, useMemo } from 'react';

import type { OptionT } from 'components/powerFlowShared/components/types';
import type { WithQueuesInjectedProps } from 'hocs';
import { withQueues } from 'hocs';

export type QueuePickerOption = { id: string; name: string } | Queue;

type FieldSelectProps = FieldSelect<QueuePickerOption>['props'];
type PurgedFieldSelectProps = Omit<FieldSelectProps, 'items' | 'input'>;

interface Props extends PurgedFieldSelectProps, WithQueuesInjectedProps {
  selectedQueue?: OptionT | null;
  input: {
    onChange: (queue: QueuePickerOption | undefined) => void;
  } & Omit<FieldSelectProps['input'], 'onChange' | 'value'>;
}

function QueuePickerImpl({
  queues,
  selectedQueue,
  input,
  ...otherProps
}: Props) {
  const {
    items: queueItems,
    searchTermChange,
    getItemsAsyncStatus,
    getItems,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = queues;
  /*
   * Because the queues are paginated the select component won't be able to display
   * the initial value unless it's in the first page.
   *
   * This is why we're adding the 'selectedQueue' as an item if it's not in the list.
   */
  const items = useMemo(() => {
    const isSelectedQueueLoaded =
      selectedQueue && queueItems.some(({ id }) => id === selectedQueue.value);

    if (!selectedQueue || isSelectedQueueLoaded) {
      return queueItems;
    }

    return [
      ...queueItems,
      {
        id: selectedQueue.value,
        name: selectedQueue.label,
      },
    ];
  }, [selectedQueue, queueItems]);
  const onChange = useCallback(
    (queueId?: string) => {
      const { onChange } = input;
      const queue = items.find(({ id }) => id === queueId);

      onChange?.(queue);
    },
    [input, items],
  );
  const onToggle = useCallback(
    (isOpen?: boolean) => {
      isOpen && searchTermChange('');
    },
    [searchTermChange],
  );

  useEffect(() => {
    getItems();
  }, [getItems]);

  const selectedItem = selectedQueue ? [selectedQueue.value] : [];
  const isLoading = getItemsAsyncStatus === 'loading';

  return (
    <FieldSelect
      items={items}
      loading={isLoading}
      onSearch={searchTermChange}
      onToggle={onToggle}
      selectedItems={selectedItem}
      pagination={{
        infinite: true,
        nextEnable: hasNextPage,
        previousEnable: hasPreviousPage,
        onNext: nextPage,
        onPrevious: previousPage,
      }}
      input={{
        ...input,
        value: selectedQueue?.value || '',
        onChange,
      }}
      searchable
      {...otherProps}
    />
  );
}

export const QueuePicker = withQueues(QueuePickerImpl, { infinite: true });
